import type { ApolloClient } from '@apollo/client';

import {
  createGatewayErrorResolver,
  hasEmptyResponderMessage,
  orderMessagesChronologically,
  resolvePromptMessageId,
  toPriorTurns,
  toQuestionLiveResultsResult,
  toQuestionTurnResult,
  toTurnMessagePayload,
  toTurnPayloadFromFragments,
  GATEWAY_ERROR_KINDS,
  type TurnMessagePayload,
} from '@/shared-responder-v2';
import type {
  AddQuestionMessageInput,
  EditQuestionMessageInput,
  InitiateSessionInput,
  InitiateSessionResult,
  LoadQuestionMessagesInput,
  LoadQuestionMessagesResult,
  QuestionLiveResultsInput,
  QuestionLiveResultsResult,
  QuestionTurnResult,
  ReportProblemInput,
  ResponderGateway,
  SkipQuestionInput,
  SurveyCompletionStatusInput,
  SurveyCompletionStatusResult,
} from '@/shared-responder-v2-core';
import { getPanelIdAndProvider, type ResponderPanel } from '@/shared-survey';
import {
  ADD_QUESTION_MESSAGE,
  Channel,
  EDIT_QUESTION_MESSAGE,
  GET_QUESTION_LIVE_RESULTS,
  GET_QUESTION_MESSAGES,
  INITIATE_UNIFIED_SESSION,
  REPORT_PROBLEM,
  SKIP_QUESTION,
  SURVEY_COMPLETION_STATUS,
  type GetQuestionMessagesQuery,
  type InitiateUnifiedSessionMutation,
} from '@/survey-graphql';

import { SURVEY_GATEWAY_ERROR_CODES } from './shared-responder-v2-gateway-error.consts';
import {
  resolvePanelRedirect,
  withPanelRedirect,
} from './shared-responder-v2-gateway-panel.helpers';

const gatewayErrors = createGatewayErrorResolver(SURVEY_GATEWAY_ERROR_CODES);

const EMPTY_QUESTION_MESSAGES: LoadQuestionMessagesResult = {
  turns: [],
  progress: null,
  promptMessageId: null,
};

type UnifiedMessage = NonNullable<
  NonNullable<GetQuestionMessagesQuery['insightV2']>['messages']
>[number];
type UnifiedQuestion = NonNullable<
  InitiateUnifiedSessionMutation['initiateUnifiedSession']['firstQuestion']
>;

/**
 * One message, handed to the shared mapper. The sender enum stops here: the
 * shared mapper reasons about the plain shape, never a generated type, so
 * everything downstream reasons about "did the responder write this", never
 * about a schema's spelling of it.
 *
 * Reads the field directly rather than through `client.readFragment`:
 * `insightV2.messages` spreads `...UnifiedMessage @unmask`
 * (`get-question-messages.gql.ts`) because this query runs `no-cache` —
 * there is no cache entry for `readFragment` to find.
 */
function toTurnMessage(
  message: UnifiedMessage | null | undefined
): TurnMessagePayload | null {
  return toTurnMessagePayload(message ?? null);
}

interface UnifiedTurn {
  readonly isQuestionComplete: boolean;
  readonly isConversationOver: boolean;
  readonly surveyProgress: number;
  readonly sent?: UnifiedMessage | null;
  readonly replyFeedback?: UnifiedMessage | null;
  readonly reply?: UnifiedMessage | null;
  readonly nextQuestion?: UnifiedQuestion | null;
  readonly thankYouMessage?: UnifiedMessage | null;
  readonly actions?: {
    readonly preActivationActionType?: string | null;
    readonly preActivationActionData?: string | null;
    readonly postActivationActionType?: string | null;
    readonly postActivationActionData?: string | null;
  } | null;
}

/**
 * Reads every turn field explicitly, so a backend rename or removal becomes a
 * compile error here rather than a silent `null` the responder was supposed to
 * render. Naming each field is what couples this adapter to the schema, and it
 * is what lets `toTurnPayloadFromFragments` stay free of any GraphQL import.
 *
 * Typed off the shared turn shape both mutations return; `addQuestionMessage`
 * additionally echoes `sent`, which `skipQuestion` has no equivalent for.
 *
 * Reads every field directly rather than through `client.readFragment`:
 * `add-question-message.gql.ts`/`skip-question.gql.ts` spread
 * `...UnifiedMessage @unmask`/`...UnifiedQuestion @unmask` — masking strips
 * `id` from these child selections (the parent only reaches them through the
 * spread, never independently), so `readFragment` cannot identify the entity
 * to read it back.
 */
function toTurnPayload(
  turn: UnifiedTurn
): Parameters<typeof toQuestionTurnResult>[0] {
  return toTurnPayloadFromFragments(
    turn,
    (message) => message,
    (question) => question
  );
}

/**
 * The port carries the channel as a plain string so the kernel never names a
 * generated enum. It is narrowed back here, and an unrecognised value becomes
 * `null` — "no channel recorded" — rather than being forced through, which
 * would fail the whole mutation over an optional attribution field.
 */
export function toChannel(channel: string | null): Channel | null {
  if (channel === null) {
    return null;
  }

  for (const known of Object.values(Channel)) {
    const knownValue: string = known;
    if (knownValue === channel) {
      return known;
    }
  }
  return null;
}

/**
 * The sole place in the responder that knows survey GraphQL field names.
 *
 * `initiateSession` is deliberately not idempotent — a second call mints a
 * second session — so nothing here retries it. `addQuestionMessage` is not
 * idempotent either. `loadQuestionMessages` is a read and is safe to repeat,
 * which is what makes resume-on-remount possible now that `openQuestion` is
 * gone.
 */
export function createApolloResponderGateway(
  client: ApolloClient,
  panel: ResponderPanel | null
): ResponderGateway {
  function toPanelAwareTurnResult(turn: UnifiedTurn): QuestionTurnResult {
    return withPanelRedirect(
      toQuestionTurnResult(toTurnPayload(turn)),
      resolvePanelRedirect(turn.actions, turn.isConversationOver, panel)
    );
  }

  return {
    async initiateSession(
      input: InitiateSessionInput
    ): Promise<InitiateSessionResult> {
      const result = await client.mutate({
        mutation: INITIATE_UNIFIED_SESSION,
        variables: {
          input: {
            ...input,
            channel: toChannel(input.channel),
            ...(panel === null ? {} : getPanelIdAndProvider(panel)),
          },
        },
        errorPolicy: 'all',
      });

      gatewayErrors.throwIfGatewayError(result.error);
      if (!result.data) {
        throw new Error('initiateUnifiedSession returned no data');
      }

      const firstQuestion = result.data.initiateUnifiedSession.firstQuestion;

      return {
        sessionId: result.data.initiateUnifiedSession.sessionId,
        firstQuestionId: firstQuestion == null ? null : firstQuestion.id,
      };
    },

    async addQuestionMessage(
      input: AddQuestionMessageInput
    ): Promise<QuestionTurnResult> {
      const result = await client.mutate({
        mutation: ADD_QUESTION_MESSAGE,
        variables: { input: { ...input, assets: input.assets?.slice() } },
        errorPolicy: 'all',
      });

      gatewayErrors.throwIfGatewayError(result.error);
      if (!result.data) {
        throw new Error('addQuestionMessage returned no data');
      }

      return toPanelAwareTurnResult(result.data.addQuestionMessage);
    },

    async editQuestionMessage(
      input: EditQuestionMessageInput
    ): Promise<QuestionTurnResult> {
      const result = await client.mutate({
        mutation: EDIT_QUESTION_MESSAGE,
        variables: { input: { ...input, assets: input.assets?.slice() } },
        errorPolicy: 'all',
      });

      gatewayErrors.throwIfGatewayError(result.error);
      if (!result.data) {
        throw new Error('editQuestionMessage returned no data');
      }

      return toPanelAwareTurnResult(result.data.editQuestionMessage);
    },

    async skipQuestion(input: SkipQuestionInput): Promise<QuestionTurnResult> {
      const result = await client.mutate({
        mutation: SKIP_QUESTION,
        variables: { input },
        errorPolicy: 'all',
      });

      gatewayErrors.throwIfGatewayError(result.error);
      if (!result.data) {
        throw new Error('skipQuestion returned no data');
      }

      return toPanelAwareTurnResult(result.data.skipQuestion);
    },

    async loadQuestionMessages(
      input: LoadQuestionMessagesInput
    ): Promise<LoadQuestionMessagesResult> {
      const result = await client.query({
        query: GET_QUESTION_MESSAGES,
        variables: { insightId: input.insightId, questionId: input.questionId },
        errorPolicy: 'all',
        fetchPolicy: 'no-cache',
      });

      /**
       * Before the first answer there is no insight row to read — the backend
       * materialises it on that first answer and `insightV2` is non-null, so it
       * rejects the whole query with `SURVEY_INSIGHTS_NOT_FOUND` rather than
       * answering with an empty conversation. That means "nothing recorded
       * yet", not "this session is lost": letting it through as the latter made
       * every session's first question restart the session, and the fresh
       * session's first read said exactly the same thing. A session id that is
       * genuinely dead is still caught, by the `addQuestionMessage` the
       * responder's own answer issues.
       */
      if (
        gatewayErrors.resolveGatewayErrorKind(result.error) ===
        GATEWAY_ERROR_KINDS.InsightNotFound
      ) {
        return EMPTY_QUESTION_MESSAGES;
      }

      gatewayErrors.throwIfGatewayError(result.error);
      if (!result.data) {
        throw new Error('insightV2.messages returned no data');
      }

      const messages = orderMessagesChronologically(
        result.data.insightV2.messages ?? []
      )
        .map(toTurnMessage)
        .filter((message): message is TurnMessagePayload => message != null);

      return {
        turns: toPriorTurns(messages),
        hasUnrenderedResponderTurn: hasEmptyResponderMessage(messages),
        promptMessageId: resolvePromptMessageId(messages),
        progress: result.data.insightV2.progress ?? null,
      };
    },

    async surveyCompletionStatus(
      input: SurveyCompletionStatusInput
    ): Promise<SurveyCompletionStatusResult> {
      const result = await client.query({
        query: SURVEY_COMPLETION_STATUS,
        variables: { insightId: input.insightId },
        errorPolicy: 'all',
        fetchPolicy: 'no-cache',
      });

      /*
       * Same "not materialised yet" rejection the resume read gets, reachable
       * here by a responder who only ever skipped: skipping records no answer,
       * so no insight row exists to ask about. Nothing is missing in that case
       * and saying so is correct rather than lenient — skipping is only offered
       * on questions the author marked optional, and the session machine's own
       * `canFinish` block is what got the responder to this call at all.
       */
      if (
        gatewayErrors.resolveGatewayErrorKind(result.error) ===
        GATEWAY_ERROR_KINDS.InsightNotFound
      ) {
        return { missingRequiredQuestions: [] };
      }

      gatewayErrors.throwIfGatewayError(result.error);
      if (!result.data) {
        throw new Error('surveyCompletionStatus returned no data');
      }

      return {
        missingRequiredQuestions:
          result.data.surveyCompletionStatus.missingRequiredQuestions.map(
            (question) => question.id
          ),
      };
    },

    async questionLiveResults(
      input: QuestionLiveResultsInput
    ): Promise<QuestionLiveResultsResult | null> {
      const result = await client.query({
        query: GET_QUESTION_LIVE_RESULTS,
        variables: { questionId: input.questionId },
        errorPolicy: 'all',
        fetchPolicy: 'no-cache',
      });

      gatewayErrors.throwIfGatewayError(result.error);
      if (!result.data) {
        throw new Error('questionLiveResults returned no data');
      }

      return toQuestionLiveResultsResult(
        result.data.questionLiveResults ?? null
      );
    },

    async reportProblem(input: ReportProblemInput): Promise<void> {
      const result = await client.mutate({
        mutation: REPORT_PROBLEM,
        variables: { input },
        errorPolicy: 'all',
      });

      gatewayErrors.throwIfGatewayError(result.error);
    },
  };
}
